import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@heartinternet/hi-ui';
import type { HostingPackageType } from './types';

const EDIT_URL = (id: number) => `/manage/manage-package-types.cgi?edit_id=${id}`;

export function useEditDropdown() {
  const [openId, setOpenId] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerElRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openDropdown = useCallback((id: number, buttonEl: HTMLButtonElement) => {
    triggerElRef.current = buttonEl;
    const rect = buttonEl.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom + 4, left: rect.right });
    setOpenId(id);
  }, []);

  const closeDropdown = useCallback(() => {
    setOpenId(null);
    setMenuPosition(null);
  }, []);

  /* v8 ignore start */
  useLayoutEffect(() => {
    if (!menuPosition || !menuRef.current) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    if (menuRect.bottom > window.innerHeight || menuRect.left < 0) {
      const triggerRect = triggerElRef.current?.getBoundingClientRect();
      if (!triggerRect) return;
      if (menuRect.bottom > window.innerHeight) {
        menuRef.current.style.top = `${triggerRect.top - menuRect.height - 4}px`;
      }
      if (menuRect.left < 0) {
        menuRef.current.style.left = `${triggerRect.left}px`;
      }
    }
  });
  /* v8 ignore stop */

  useEffect(() => {
    if (openId === null) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        (triggerElRef.current && triggerElRef.current.contains(target)) ||
        (menuRef.current && menuRef.current.contains(target))
      ) {
        return;
      }
      closeDropdown();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openId, closeDropdown]);

  const renderTrigger = useCallback(
    (row: HostingPackageType) => {
      const isOpen = openId === row.id;
      return (
        <div
          className={`hi-ui-hosting-package-types-page__cog-wrapper${isOpen ? ' hi-ui-hosting-package-types-page__cog-wrapper--open' : ''}`}
          /* v8 ignore start -- hover timing logic untestable in JSDOM */
          onMouseEnter={(e) => {
            if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
            const btn = (e.currentTarget as HTMLElement).querySelector(
              '.hi-ui-hosting-package-types-page__cog-trigger'
            ) as HTMLButtonElement | null;
            if (btn) openDropdown(row.id, btn);
          }}
          onMouseLeave={() => {
            if (isOpen) {
              hoverTimeoutRef.current = setTimeout(() => closeDropdown(), 150);
            }
          }}
          /* v8 ignore stop */
        >
          <button
            className="hi-ui-hosting-package-types-page__cog-trigger"
            onClick={(e) => {
              e.stopPropagation();
              if (isOpen) {
                closeDropdown();
              } else {
                openDropdown(row.id, e.currentTarget);
              }
            }}
            aria-label={`Manage ${row.name}`}
          >
            <Icon name="cog" size={20} />
          </button>
        </div>
      );
    },
    [openId, openDropdown, closeDropdown]
  );

  const renderPortal = useCallback(() => {
    if (openId === null || !menuPosition) return null;

    return createPortal(
      <div
        ref={menuRef}
        className="hi-ui-hosting-package-types-page__dropdown"
        style={{
          position: 'fixed',
          top: menuPosition.top,
          left: menuPosition.left,
          transform: 'translateX(-100%)',
        }}
        /* v8 ignore start -- hover timing logic untestable in JSDOM */
        onMouseEnter={() => {
          if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        }}
        onMouseLeave={() => {
          hoverTimeoutRef.current = setTimeout(() => closeDropdown(), 150);
        }}
        /* v8 ignore stop */
      >
        <a href={EDIT_URL(openId)} className="hi-ui-hosting-package-types-page__dropdown-item">
          Edit
        </a>
      </div>,
      document.body
    );
  }, [openId, menuPosition, closeDropdown]);

  return { renderTrigger, renderPortal };
}
